/**
 * locale-contracts.ts
 *
 * Core data contracts for dynamic globalization, locale settings,
 * translation records, and multilingual telemetry.
 */

export type Bcp47LocaleTag = 'en' | 'zh-Hans' | 'es' | 'fr' | 'de' | 'ar' | 'sw' | 'lg';

export type TextDirection = 'ltr' | 'rtl';

export interface LocaleDefinition {
  readonly locale: Bcp47LocaleTag;
  readonly languageName: string;
  readonly nativeName: string;
  readonly direction: TextDirection;
  readonly fallbackLocale: Bcp47LocaleTag;
  readonly dateTimeLocale: string;
  readonly flagEmoji: string;
  readonly enabled: boolean;
}

export interface LocalePreference {
  readonly locale: Bcp47LocaleTag;
  readonly source: 'user' | 'workspace' | 'browser' | 'default';
  readonly updatedAt: string;
}

export interface TranslationParams {
  readonly [key: string]: string | number | boolean | undefined;
}

export interface TranslationRequest {
  readonly text: string;
  readonly sourceLocale?: string;
  readonly targetLocale: Bcp47LocaleTag;
  readonly contentType: 'message' | 'task' | 'crm-note' | 'agent-output';
  readonly workspaceId: string;
  readonly consent: 'user-requested' | 'workspace-policy';
}

export interface TranslationResult {
  readonly translatedText: string;
  readonly sourceLocale: string;
  readonly targetLocale: Bcp47LocaleTag;
  readonly providerId: string;
  readonly contentHash: string;
  readonly generatedAt: string;
  readonly disclaimer: string;
}

export const SUPPORTED_LOCALES: Record<Bcp47LocaleTag, LocaleDefinition> = {
  en: {
    locale: 'en',
    languageName: 'English',
    nativeName: 'English (US)',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'en-US',
    flagEmoji: '🇺🇸',
    enabled: true,
  },
  'zh-Hans': {
    locale: 'zh-Hans',
    languageName: 'Chinese (Simplified)',
    nativeName: '简体中文',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'zh-CN',
    flagEmoji: '🇨🇳',
    enabled: true,
  },
  es: {
    locale: 'es',
    languageName: 'Spanish',
    nativeName: 'Español',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'es-ES',
    flagEmoji: '🇪🇸',
    enabled: true,
  },
  fr: {
    locale: 'fr',
    languageName: 'French',
    nativeName: 'Français',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'fr-FR',
    flagEmoji: '🇫🇷',
    enabled: true,
  },
  de: {
    locale: 'de',
    languageName: 'German',
    nativeName: 'Deutsch',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'de-DE',
    flagEmoji: '🇩🇪',
    enabled: true,
  },
  ar: {
    locale: 'ar',
    languageName: 'Arabic',
    nativeName: 'العربية',
    direction: 'rtl',
    fallbackLocale: 'en',
    dateTimeLocale: 'ar-SA',
    flagEmoji: '🇸🇦',
    enabled: true,
  },
  sw: {
    locale: 'sw',
    languageName: 'Kiswahili',
    nativeName: 'Kiswahili',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'sw-TZ',
    flagEmoji: '🇹🇿',
    enabled: true,
  },
  lg: {
    locale: 'lg',
    languageName: 'Oluganda',
    nativeName: 'Oluganda',
    direction: 'ltr',
    fallbackLocale: 'en',
    dateTimeLocale: 'lg-UG',
    flagEmoji: '🇺🇬',
    enabled: true,
  },
};

export interface UserLocaleSettings {
  readonly language: string;          // e.g. "ar", "en", "sw", "lg", "fr", "es"
  readonly locale: string;            // e.g. "ar-EG", "en-US", "sw-TZ", "fr-FR"
  readonly direction: 'ltr' | 'rtl';  // CSS dir attribute
  readonly timezone: string;          // IANA timezone, e.g. "Asia/Kuala_Lumpur", "Africa/Kampala"
  readonly currency: string;          // ISO 4217 code, e.g. "USD", "EUR", "UGX", "KES"
  readonly numberFormat: 'standard' | 'compact';
}

export type TranslationStatus = 'draft' | 'review' | 'approved' | 'published';
export type TranslatorType = 'human' | 'ai' | 'hybrid';

export interface TranslationRecord {
  readonly contentId: string;
  readonly sourceLocale: string;
  readonly targetLocale: string;
  readonly sourceVersion: string;
  readonly translatedVersion?: string;
  readonly title: string;
  readonly body: string;
  readonly status: TranslationStatus;
  readonly translator: TranslatorType;
  readonly reviewedBy?: string;
  readonly publishedAt?: number;
}

export interface MultilingualAgentTrace {
  readonly interactionId: string;
  readonly inputLanguage: string;
  readonly outputLanguage: string;
  readonly retrievalLanguage: string;
  readonly translationFallbackUsed: boolean;
  readonly locale: string;
  readonly timezone: string;
  readonly timestamp: number;
}
